declare type PluginInstallFunction = (app: any, ...options: any[]) => void;
import IconComponent from "@/components/IconComponent.vue";
import SparklineComponent from "@/components/SparklineComponent.vue";
import StockImageTag from "@/components/StockImageTag.vue";
import ElementPlus from 'element-plus';
import en from "element-plus/es/locale/lang/en";
import ar from "element-plus/es/locale/lang/ar";

import 'element-plus/dist/index.css';
import { config } from 'jenesius-vue-modal';
import { createApp, ref } from 'vue';
import InlineSvg from 'vue-inline-svg';
import VueSocialSharing from 'vue-social-sharing';
// import "bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import draggable from "vuedraggable";
import App from './App.vue';
import './assets/scss/global.scss';
import './assets/scss/officefieldoverridding.scss';
import './assets/scss/utility.scss';
import filters from './helpers/filters';
import setup from './interceptors/http.interceptor';
import './registerServiceWorker';
import router from './router/router';
import routerGuard from './router/router-guard';
import mitt from "mitt";
import MaterializeSelect from './components/material-select/MaterialSelect.vue';
import PlayBookItem from './views/private/profile-screen/components/PlayBookComponent/PlayBookItem.vue';
import HighchartsVue from 'highcharts-vue'
import Highcharts from 'highcharts'
import StockModule from 'highcharts/modules/stock';
import VueApexCharts from "vue3-apexcharts";
// import VueSplide from '@splidejs/vue-splide';
import PostCardComponent from "./views/private/timeline/components/posts/PostCardComponent.vue";
import { Calendar, DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import { initNewRelic } from './helpers/tracking.helpers';
import { VueQueryPlugin } from '@tanstack/vue-query'
import './aws-exports';
import Logo from "./components/common/Logo.vue";
import { createPinia } from 'pinia';
import PopoverMenu from "@/components/common/Dropdown/index.vue";
import { Icon } from '@iconify/vue'
import { createConfig, http, WagmiPlugin } from "@wagmi/vue";
import { mainnet } from "@wagmi/vue/chains";
import { coinbaseWallet, injected, metaMask } from "@wagmi/vue/connectors";
import { getPersistedLocale, setupI18n } from '@/locale/index'
import { GlobalMemoryMixin } from '@/mixins/globalMemoryMixin';
import {
  initKeycloakSsoSync,
} from '@/services/keycloak-sso-sync';
import {
  hasValidStoredSession,
  refreshKeycloakSessionOnReload,
} from '@/services/keycloak.service';

setup(router);
routerGuard(router);
initNewRelic({
  applicationID: process.env.VITE_NEW_RELIC_APP_ID || '1134517327',
  licenseKey: process.env.VITE_NEW_RELIC_LICENSE_KEY || 'NRBR-7b46cbcc5ac78395603',
  release: {
    name: process.env.VITE_APP_NAME || 'Traderverse profile',
    version: process.env.VITE_APP_VERSION || '1.4.4'
  }
});

StockModule(Highcharts)

const pinia = createPinia()
const app = createApp(App);

// Initialize i18n first, then setup the app
async function initializeApp() {
  try {
    // Initialize i18n
    const i18n = await setupI18n();
    // console.log('i18n initialized successfully');
    app.mixin(GlobalMemoryMixin);

    // Setup all plugins and components
    app.use(i18n); // This must come before other plugins that might use i18n
    app.use(VueQueryPlugin);
    app.use(router);
    app.use(Toast);
    app.use(HighchartsVue as unknown as PluginInstallFunction);

    app.component('draggable', draggable);
    app.component('SparklineComponent', SparklineComponent);
    app.component('IconComponent', IconComponent);
    app.component('LogoComponent', Logo);
    app.component("apexchart", VueApexCharts);
    app.component('StockImageTag', StockImageTag);
    app.component('inline-svg', InlineSvg);
    app.component('Icon', Icon);
    app.component('MaterialSelect', MaterializeSelect);
    app.component('PlayBookItem', PlayBookItem);
    const locale = getPersistedLocale();
    const currentLocale = ref(locale === 'en' ? en : ar);
    app.use(ElementPlus, { locale: currentLocale.value });
    // app.use(MomentPlugin);
    app.use(VueSocialSharing);
    app.component('VCalendar', Calendar);
    app.component('VDatePicker', DatePicker);

    app.config.globalProperties.$filters = filters;
    app.config.globalProperties.$msalInstance = {};
    app.config.globalProperties.emitter = mitt();

    config({
      animation: undefined,
      scrollLock: true,
    });

    app.component("PostCardComponent", PostCardComponent);
    app.component("PopoverMenu", PopoverMenu);

    const wagmiConfig = createConfig({
      chains: [mainnet],
      connectors: [
        injected(),
        metaMask(),
        coinbaseWallet({ appName: "Traderverse Profile" }),
      ],
      transports: {
        [mainnet.id]: http(),
      },
    });
    app.use(WagmiPlugin, { config: wagmiConfig });
    app.use(pinia);

    await refreshKeycloakSessionOnReload();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && hasValidStoredSession()) {
        void refreshKeycloakSessionOnReload();
      }
    });
    initKeycloakSsoSync();

    // Mount the app
    app.mount('#app');
    // console.log('App mounted successfully');

  } catch (error) {
    console.error('Failed to initialize app:', error);
  }
}

// Start the app initialization
initializeApp();